<template>
    <div class="tw-px-4">
        <!-- Loading State -->
        <div v-if="loading" class="tw-max-w-4xl tw-mx-auto">
            <!-- Main Content Skeleton -->
            <div
                class="tw-w-full tw-min-h-[200px] md:tw-min-h-[300px] tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-primary-200 tw-overflow-hidden">
                <!-- Background Image Skeleton - Reduced height on mobile -->
                <div class="tw-h-[150px] md:tw-h-[250px] tw-w-full tw-bg-gray-200 tw-animate-pulse tw-relative">
                </div>

                <!-- Company Info Skeleton - Responsive layout -->
                <div
                    class="tw-px-4 tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-gap-4 tw-relative tw-z-20">
                    <!-- Logo Skeleton - Smaller on mobile -->
                    <div
                        class="tw-w-[120px] tw-h-[120px] md:tw-w-[200px] md:tw-h-[200px] tw-bg-gray-200 tw-rounded-lg -tw-translate-y-1/3 md:-tw-translate-y-1/3 tw-animate-pulse tw-border tw-border-solid tw-border-gray-200 tw-mx-auto md:tw-mx-0">
                    </div>

                    <!-- Company Details Skeleton - Centered on mobile -->
                    <div
                        class="tw-pt-2 md:tw-pt-8 tw-flex-1 tw-text-center md:tw-text-left md:-tw-translate-y-[50px] -tw-translate-y-2/3">
                        <div
                            class="tw-h-6 md:tw-h-8 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-min-w-[150px] tw-w-3/4 tw-mx-auto md:tw-mx-0 tw-mb-2">
                        </div>
                        <div class="tw-flex tw-items-center tw-justify-center md:tw-justify-start tw-gap-2 tw-mt-2">
                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            <div
                                class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/2 tw-min-w-[100px]">
                            </div>
                        </div>
                    </div>
                </div>

                <!-- About Company Section Skeleton -->
                <div class="tw-px-4 md:-tw-mt-8 -tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/3 tw-mb-2"></div>
                    <div class="tw-space-y-2">
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-full"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-5/6"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-4/5"></div>
                    </div>
                </div>

                <!-- Categories Section Skeleton -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/4 tw-mb-2"></div>
                    <div class="tw-flex tw-flex-wrap tw-gap-2">
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-20"></div>
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-24"></div>
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-16"></div>
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-28"></div>
                    </div>
                </div>

                <!-- Certificates Section Skeleton -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/3 tw-mb-2"></div>
                    <div class="tw-flex tw-flex-wrap tw-gap-2">
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-24"></div>
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-32"></div>
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-20"></div>
                    </div>
                </div>

                <!-- Team Members Section Skeleton -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/3 tw-mb-4"></div>
                    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                        <!-- Team Member Card Skeleton 1 -->
                        <div class="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-4 tw-flex tw-items-center tw-gap-3">
                            <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-200 tw-animate-pulse tw-flex-shrink-0"></div>
                            <div class="tw-flex-1 tw-min-w-0">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-3/4 tw-mb-2"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/2"></div>
                            </div>
                        </div>
                        <!-- Team Member Card Skeleton 2 -->
                        <div class="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-4 tw-flex tw-items-center tw-gap-3">
                            <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-200 tw-animate-pulse tw-flex-shrink-0"></div>
                            <div class="tw-flex-1 tw-min-w-0">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-2/3 tw-mb-2"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/3"></div>
                            </div>
                        </div>
                        <!-- Team Member Card Skeleton 3 -->
                        <div class="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-4 tw-flex tw-items-center tw-gap-3">
                            <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-200 tw-animate-pulse tw-flex-shrink-0"></div>
                            <div class="tw-flex-1 tw-min-w-0">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-4/5 tw-mb-2"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-2/5"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Social Media Section Skeleton -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/3 tw-mb-4"></div>
                    <div class="tw-flex tw-flex-wrap tw-gap-3">
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-w-24"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-w-20"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-w-28"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-w-24"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-w-28"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-w-24"></div>
                    </div>
                </div>

                <!-- Products Section Skeleton -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/4 tw-mb-4"></div>
                    <div class="tw-text-center tw-py-6">
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/2 tw-mx-auto"></div>
                    </div>
                </div>

                <!-- Services Section Skeleton -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4 tw-pb-8">
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/4 tw-mb-4"></div>
                    <div class="tw-text-center tw-py-6">
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/2 tw-mx-auto"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="tw-text-center tw-py-12 tw-max-w-md tw-mx-auto">
            <icon icon="solar:buildings-2-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
                {{ $t('errors.company-not-found', 'Company not found') }}
            </h3>
            <p class="tw-text-gray-500 tw-mb-6">
                {{ $t('errors.company-not-found-description', 'The company you are looking for could not be found')
                }}
            </p>
            <a-button as="router-link" :to="{ name: 'shared.companies' }" intent="primary"
                left-icon="solar:arrow-left-bold-duotone">
                {{ $t('buttons.back-to-companies', 'Back to Companies') }}
            </a-button>
        </div>

        <!-- Loaded Content -->
        <div v-else-if="company" class="tw-max-w-4xl tw-mx-auto">
            <!-- Main Content -->
            <div
                class="tw-w-full tw-pb-8 tw-min-h-[200px] md:tw-min-h-[300px] tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-primary-200 tw-overflow-hidden">
                <!-- Background Image - Reduced height on mobile -->
                <div
                    class="tw-h-[150px] md:tw-h-[250px] tw-w-full tw-bg-gray-100 tw-relative tw-border-b tw-border-solid tw-border-gray-200">
                    <img v-if="company.background_image" :src="company.background_image"
                        :alt="`${company.name} Background`"
                        class="tw-w-full tw-h-full tw-object-contain tw-absolute tw-inset-0 tw-z-10" />
                    <!-- Blurry background cover -->
                    <div v-if="company.background_image" class="tw-w-full tw-h-full tw-absolute tw-inset-0 tw-z-0"
                        :style="{
                            backgroundImage: `url(${company.background_image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: 'blur(10px)',
                            opacity: '0.3'
                        }"></div>
                    <div v-else class="tw-w-full tw-h-full tw-bg-gradient-to-r tw-from-gray-200 tw-to-gray-300">
                    </div>
                    <div class="tw-absolute tw-z-20 tw-inset-0 tw-bg-gradient-to-t tw-from-black/20 tw-opacity-50">
                    </div>
                </div>

                <!-- Company Info - Responsive layout -->
                <div class="tw-px-4 tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-relative tw-z-20">
                    <!-- Logo - Smaller on mobile -->
                    <div
                        class="tw-w-[120px] tw-h-[120px] md:tw-w-[200px] md:tw-h-[200px] tw-bg-gray-100 tw-rounded-lg -tw-translate-y-1/3 md:-tw-translate-y-1/3 tw-flex tw-items-center tw-justify-center tw-border tw-border-solid tw-border-gray-200 tw-overflow-hidden tw-relative tw-mx-auto md:tw-mx-0">
                        <!-- Blurry background cover for logo -->
                        <div v-if="company.logo" class="tw-w-full tw-h-full tw-absolute tw-inset-0 tw-z-0" :style="{
                            backgroundImage: `url(${company.logo})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: 'blur(10px)',
                            opacity: '0.3'
                        }"></div>
                        <img v-if="company.logo" :src="company.logo" :alt="`${company.name} Logo`"
                            class="tw-w-full tw-h-full tw-object-contain tw-relative tw-z-10" />
                        <icon v-else icon="solar:buildings-2-bold-duotone"
                            class="tw-text-4xl md:tw-text-6xl tw-text-gray-400 tw-relative tw-z-10" />
                    </div>

                    <!-- Company Details - Centered on mobile -->
                    <div
                        class="tw-pt-2 md:tw-pt-8 tw-text-center md:tw-text-left md:tw-translate-y-0 -tw-translate-y-2/3">
                        <h1 class="tw-text-xl md:tw-text-3xl tw-font-bold tw-text-primary-800 tw-mb-2">{{
                            company.name || 'Company Name' }}</h1>
                        <div class="tw-flex tw-items-center tw-justify-center md:tw-justify-start tw-gap-2">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <icon icon="mingcute:location-fill"
                                    class="tw-text-primary-800 tw-text-sm md:tw-text-base" />
                                <p class="tw-text-xs md:tw-text-sm tw-text-gray-500">{{
                                    company.country || company.address || 'Location not specified' }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- About Company Section -->
                <div class="tw-px-4 md:-tw-mt-8 -tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.about-company', { company: company.name }) }}</h1>
                    <p class="tw-text-sm tw-text-gray-500 tw-mb-4" v-html="company.description?.replace(/\n/g, '<br>')"></p>
                </div>

                <!-- Categories Section -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.categories') }}</h1>
                    <div class="tw-flex tw-flex-wrap tw-gap-2">
                        <div v-for="category in company.categories" :key="category" class="tw-bg-primary-100 tw-rounded-full tw-px-4 tw-py-2 tw-text-primary-800 tw-text-sm">
                            {{ category }}
                        </div>
                    </div>
                </div>

                <!-- Certificates Section -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.certificates') }}</h1>
                    <div class="tw-flex tw-flex-wrap tw-gap-2">
                        <div v-for="certificate in company.certificates" :key="certificate" class="tw-text-primary-100 tw-bg-primary-800 tw-rounded-full tw-px-4 tw-py-2  tw-text-sm">
                            {{ certificate }}
                        </div>
                    </div>
                </div>

                <!-- Team Members Section -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-4">{{ $t('common.team-members') }}</h1>
                    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                        <div v-for="user in company.users" :key="user.id" 
                            class="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-4 tw-flex tw-items-center tw-gap-3 hover:tw-shadow-md tw-transition-shadow">
                            <!-- Profile Image -->
                            <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-flex-shrink-0">
                                <img v-if="user.profile_image" 
                                    :src="user.profile_image" 
                                    :alt="`${user.name} profile`"
                                    class="tw-w-full tw-h-full tw-object-cover" />
                                <icon v-else 
                                    icon="solar:user-bold-duotone" 
                                    class="tw-text-gray-400 tw-text-xl" />
                            </div>
                            
                            <!-- User Info -->
                            <div class="tw-flex-1 tw-min-w-0">
                                <h3 class="tw-font-medium tw-text-gray-900 tw-text-sm tw-truncate">
                                    {{ user.name }}
                                </h3>
                                <p class="tw-text-xs tw-text-gray-500 tw-truncate">
                                    {{ user.role || $t('common.member') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Social Media Section -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-4">{{ $t('common.social-media') }}</h1>
                    <div class="tw-flex tw-flex-wrap tw-gap-3">
                        <!-- Facebook -->
                        <a v-if="company.facebook" 
                            :href="company.facebook" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-transition-colors">
                            <icon icon="ri:facebook-fill" class="tw-text-blue-600 tw-text-lg" />
                            <span>Facebook</span>
                        </a>
                        
                        <!-- Twitter/X -->
                        <a v-if="company.twitter" 
                            :href="company.twitter" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-transition-colors">
                            <icon icon="hugeicons:new-twitter" class="tw-text-black tw-text-lg" />
                            <span>X</span>
                        </a>
                        
                        <!-- Instagram -->
                        <a v-if="company.instagram" 
                            :href="company.instagram" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-transition-colors">
                            <icon icon="proicons:instagram" class="tw-text-pink-600 tw-text-lg" />
                            <span>Instagram</span>
                        </a>
                        
                        <!-- LinkedIn -->
                        <a v-if="company.linkedin" 
                            :href="company.linkedin" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-transition-colors">
                            <icon icon="ri:linkedin-fill" class="tw-text-blue-700 tw-text-lg" />
                            <span>LinkedIn</span>
                        </a>
                        
                        <!-- YouTube -->
                        <a v-if="company.youtube" 
                            :href="company.youtube" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-transition-colors">
                            <icon icon="famicons:logo-youtube" class="tw-text-red-600 tw-text-lg" />
                            <span>YouTube</span>
                        </a>
                        
                        <!-- Website -->
                        <a v-if="company.website" 
                            :href="company.website" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-transition-colors">
                            <icon icon="solar:earth-bold-duotone" class="tw-text-gray-600 tw-text-lg" />
                            <span>Website</span>
                        </a>
                        
                        <!-- No social media message -->
                        <div v-if="!hasSocialMedia" class="tw-text-gray-500 tw-text-sm tw-italic">
                            {{ $t('common.no-social-media-available') }}
                        </div>
                    </div>
                </div>

                <!-- Products Section -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-4">{{ $t('common.products') }}</h1>
                    <div class="tw-text-center tw-py-6">
                        <p class="tw-text-gray-500 tw-text-sm">{{ $t('common.no-products-available') }}</p>
                    </div>
                </div>

                <!-- Services Section -->
                <div class="tw-px-4 tw-mt-4 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-4">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-4">{{ $t('common.services') }}</h1>
                    <div class="tw-text-center tw-py-6">
                        <p class="tw-text-gray-500 tw-text-sm">{{ $t('common.no-services-available') }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import companyApi from '@/api/companies';
import useAlert from '@/composables/useAlert';

const route = useRoute();
const { t } = useI18n();

// Reactive data
const loading = ref(true);
const company = ref(null);
const error = ref(false);

// Fetch company data
const fetchCompanyData = async () => {
    loading.value = true;
    error.value = false;

    try {
        // Get company ID from route parameters
        const companyId = route.params.id;
        
        const response = await companyApi.get(companyId);
        console.log('Response:', response);
        console.log('Response Data:', response.data);
        company.value = response.data.data;
    } catch (err) {
        console.error('Error fetching company data:', err);
        error.value = true;
        useAlert(t('errors.failed-to-load-company', 'Failed to load company data'), 'danger');
    } finally {
        loading.value = false;
    }
};

// Initialize component
onMounted(() => {
    fetchCompanyData();
});

// Computed property to check if the company has any social media links
const hasSocialMedia = computed(() => {
    return company.value && (
        company.value.facebook ||
        company.value.twitter ||
        company.value.linkedin ||
        company.value.instagram ||
        company.value.youtube ||
        company.value.website
    );
});
</script>

<style scoped>

</style>